import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

/**
 * Convert a file or blob to base64 data URL for preview purposes
 * @param {File|Blob} file - The file or blob to convert
 * @returns {Promise<string>} Base64 data URL
 */
export const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

/**
 * Robust image detection that works across web and native platforms
 * Checks file.type, file.name extension, and URL extension as fallbacks
 * @param {Object} file - File object with type, name properties
 * @param {string} [url] - Optional file URL to check
 * @returns {boolean} True if file is an image
 */
export const isImageFile = (file, url = null) => {
    if (!file) return false;

    // Check MIME type (may be empty on Android native)
    const mimeType = file.type || file.mime_type;
    if (mimeType && mimeType.startsWith('image/')) {
        return true;
    }

    // Check file extension
    if (file.name && /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)$/i.test(file.name)) {
        return true;
    }

    // Check URL extension as last resort
    const cleanUrl = typeof url === 'string' ? url.split('?')[0] : '';
    if (cleanUrl && /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)$/i.test(cleanUrl)) {
        return true;
    }

    return false;
};

/**
 * Generate a local thumbnail for instant preview
 * @param {File} file - The file to generate thumbnail for
 * @param {string} [url] - Optional URL to check if it's an image
 * @returns {Promise<string|null>} Base64 thumbnail or null
 */
export const generateThumbnail = async (file, url = null) => {
    if (!isImageFile(file, url)) {
        return null;
    }

    try {
        return await getBase64(file);
    } catch (e) {
        console.error('Thumbnail generation failed:', e);
        return null;
    }
};

/**
 * Convert base64 data URL to File object
 * @param {string} base64String - Base64 data URL
 * @param {string} fileName - Name for the file
 * @returns {Promise<File>} File object
 */
export const base64ToFile = async (base64String, fileName = 'image.jpg') => {
    const response = await fetch(base64String);
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
};

/**
 * Pick an image using native Capacitor Camera plugin on mobile, or trigger file input on web
 * @param {Object} options - Camera options
 * @param {boolean} options.allowEditing - Allow image editing (default: true)
 * @param {number} options.quality - Image quality 0-100 (default: 90)
 * @param {number} options.width - Max width (optional)
 * @param {number} options.height - Max height (optional)
 * @param {'GALLERY'|'CAMERA'|'PROMPT'} options.source - Source type (default: 'PROMPT')
 * @returns {Promise<File|null>} Selected file or null if cancelled
 */
export const pickImageNative = async (options = {}) => {
    const {
        allowEditing = true,
        quality = 90,
        width,
        height,
        source = 'PROMPT'
    } = options;

    // Only use Camera plugin on native platforms
    if (!Capacitor.isNativePlatform()) {
        return null; // Fall back to standard HTML input
    }

    try {
        // Map source string to CameraSource enum
        let cameraSource = CameraSource.Prompt;
        if (source === 'GALLERY') {
            cameraSource = CameraSource.Photos;
        } else if (source === 'CAMERA') {
            cameraSource = CameraSource.Camera;
        }

        const cameraOptions = {
            quality,
            allowEditing,
            resultType: CameraResultType.DataUrl,
            source: cameraSource,
            ...(width && { width }),
            ...(height && { height }),
        };

        const image = await Camera.getPhoto(cameraOptions);

        // Convert base64 result to File object
        if (image.dataUrl) {
            // Generate filename with proper extension
            const format = image.format || 'jpeg';
            const fileName = `photo_${Date.now()}.${format}`;
            return await base64ToFile(image.dataUrl, fileName);
        }

        return null;
    } catch (error) {
        // User cancelled or permission denied
        if (error.message && (error.message.includes('cancel') || error.message.includes('Cancel'))) {
            return null;
        }
        // Re-throw other errors
        throw error;
    }
};

/**
 * Check if we should use native file picker
 * @returns {boolean} True if on native platform
 */
export const shouldUseNativePicker = () => {
    return Capacitor.isNativePlatform();
};

/**
 * Process uploaded file for consistent handling across web and native
 * @param {File} file - The uploaded file
 * @param {Object} uploadedData - Data returned from upload API
 * @param {boolean} isNative - Whether running on native platform (determines thumbnail generation)
 * @returns {Promise<Object>} Processed file object with consistent structure
 */
export const processUploadedFile = async (file, uploadedData, isNative = false) => {
    const isImage = isImageFile(file, uploadedData?.file_url);

    // Only generate thumbnail for native platforms OR when no file_url available
    // On web, use API's file_url directly for performance (no base64 conversion needed)
    const thumbUrl = (isNative || !uploadedData?.file_url)
        ? await generateThumbnail(file, uploadedData?.file_url)
        : (isImage ? uploadedData.file_url : null);

    return {
        uid: `-${Date.now()}`,
        name: file.name,
        status: 'done',
        url: isImage ? uploadedData?.file_url : null,
        thumbUrl: thumbUrl,
        fileId: uploadedData?.id || null,
        fileData: uploadedData,
        type: file.type || uploadedData?.mime_type,
        isImage,
    };
};

/**
 * Request camera permissions (for native platforms)
 * @returns {Promise<boolean>} True if permissions granted
 */
export const requestCameraPermissions = async () => {
    if (!Capacitor.isNativePlatform()) {
        return true; // No permissions needed on web
    }

    try {
        const permissions = await Camera.checkPermissions();

        if (permissions.camera === 'granted' && permissions.photos === 'granted') {
            return true;
        }

        const result = await Camera.requestPermissions({
            permissions: ['camera', 'photos']
        });

        return result.camera === 'granted' && result.photos === 'granted';
    } catch (error) {
        console.error('Permission request failed:', error);
        return false;
    }
};
